import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SVG from './svg';

const Fullscreen = styled.div`
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBlock = styled.div`
  width: 620px;
  max-width: calc(100vw - 32px);
  background: white;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const TitleBlock = styled.div`
  .titleBox {
    width: 100%;
    height: 56px;
    background: #eeeff0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    box-sizing: border-box;
  }

  .titleText {
    font-family: 'Source Sans Pro';
    font-size: 20px;
    font-weight: 600;
    color: #59616a;
  }

  .closeIcon {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ContentBlock = styled.div`
  padding: 18px 24px 10px;
  color: #59616a;
  font-family: 'Source Sans Pro';
`;

const Subtitle = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: #7a838d;
`;

const ButtonBlock = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  padding: 18px 24px 24px;
  border-top: 1px solid #eeeff0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ChoiceCard = styled.button`
  appearance: none;
  border: 1px solid #e6e8ea;
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  cursor: pointer;
  text-align: left;

  display: grid;
  grid-template-rows: auto auto;
  gap: 12px;

  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${(props) => props.theme?.main_color || '#ff6d22'};
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme?.main_color || '#ff6d22'};
    outline-offset: 2px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: contain;
  display: block;

  @media (max-width: 640px) {
    height: 200px;
  }
`;

const CardTitle = styled.div`
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-weight: 600;
  color: #59616a;
`;

const CardHint = styled.div`
  font-family: 'Source Sans Pro';
  font-size: 13px;
  color: #7a838d;
  margin-top: 2px;
`;

const DOWNLOAD_LINKS = {
  OVO: {
    standard: 'https://3disc.sharepoint.com/:u:/s/software.release/EdJunPmDZd1EuNzvs16VT7ABDf745H3SgVoccdWkVuylzQ?e=O2xifj&download=1',
    blackwell: 'https://3disc.sharepoint.com/:u:/s/software.release/IQDRi9LiK66mQ4aYmDtgDM1MAWRodZNu0mmnCIbbLnK5WSw?e=nMCeeI&download=1',
  },
  OVO3: {
    standard: 'https://3disc.sharepoint.com/:u:/s/software.release/IQBol6WRxJwCT7hQREjz78ZFARCOUjid0Vwlgv9lDD_fF5c?e=1f7EgS&download=1',
    blackwell: 'https://3disc.sharepoint.com/:u:/s/software.release/IQDjCcD870YySatGiRYZ21R-AWagLadO1nfyY7uGIloGtK0?e=FgNbJK&download=1',
  },
};

// is BLACKWELL GPU ?
const isBlackwellGPU = (renderer) => {
  if (!renderer) return false;

  const blackwellPatterns = [
    /rtx\s*50\d{2}/i,      // RTX 5090, 5080, 5070, etc.
    /rtx\s*5\d{3}/i,       // RTX 5xxx
    /geforce\s*rtx\s*50/i, // GeForce RTX 50xx
    /blackwell/i,          // Direct Blackwell mention
  ];

  for (const pattern of blackwellPatterns) {
    if (pattern.test(renderer)) {
      return true;
    }
  }

  return false;
};

const GPUDetectionModal = ({ show, onClose }) => {
  const { t } = useTranslation();
  const { odm } = useSelector(({ app }) => ({ odm: app.odm }));

  const [loading, setLoading] = useState(true);
  const [gpuInfo, setGpuInfo] = useState({ renderer: null, isBlackwell: false });

  useEffect(() => {
    if (show) detectGPU();
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, onClose]);

  const detectGPU = async () => {
    setLoading(true);

    let renderer = null;
    let isBlackwell = false;

    // WebGL Detection
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      } else {
        renderer = gl.getParameter(gl.RENDERER);
      }
      
      isBlackwell = isBlackwellGPU(renderer);
    }

    setGpuInfo({
      renderer: renderer || t('Not available'),
      isBlackwell: isBlackwell,
    });

    setLoading(false);
  };

  const handleDownloadOVO = () => {
    const url = gpuInfo.isBlackwell
      ? DOWNLOAD_LINKS.OVO.blackwell
      : DOWNLOAD_LINKS.OVO.standard;
    window.open(url, '_blank');
    onClose();
  };

  const handleDownloadOVO3 = () => {
    const url = gpuInfo.isBlackwell
      ? DOWNLOAD_LINKS.OVO3.blackwell
      : DOWNLOAD_LINKS.OVO3.standard;
    window.open(url, '_blank');
    onClose();
  };

  if (!show) return null;

  const ovoImg = `${process.env.PUBLIC_URL}/images/download/ovo-540.png`;
  const ovo3Img = `${process.env.PUBLIC_URL}/images/download/ovo3-540.png`;

  return (
    <Fullscreen onClick={onClose}>
      <ModalBlock
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('Download Software')}
      >
        <TitleBlock>
          <div className="titleBox">
            <div className="titleText">{t('Download Software')}</div>
            <div className="closeIcon" onClick={onClose}>
              <SVG
                name="close"
                color="#878D93"
                colorOver={odm?.main_color_hover || '#ff6d22'}
                colorOut="#878D93"
                colorDown={odm?.main_color || '#ff6d22'}
              />
            </div>
          </div>
        </TitleBlock>

        <ContentBlock>
          <Subtitle>
            {loading ? t('Preparing the best download for your GPU…') : t('Choose your software to download:')}
          </Subtitle>
        </ContentBlock>

        <ButtonBlock>
          <ChoiceCard type="button" onClick={handleDownloadOVO} disabled={loading} aria-label={t('Download OVO')}>
            <CardImage
              src={ovoImg}
              alt="OVO"
              onError={(e) => {
                // fallback (au cas où un proxy retire le /r)
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/images/download/ovo-540.png';
              }}
            />
            <div>
              <CardTitle>OVO</CardTitle>
              <CardHint>{t('Download for Windows')}</CardHint>
            </div>
          </ChoiceCard>

          <ChoiceCard type="button" onClick={handleDownloadOVO3} disabled={loading} aria-label={t('Download OVO 3')}>
            <CardImage
              src={ovo3Img}
              alt="OVO 3"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/images/download/ovo3-540.png';
              }}
            />
            <div>
              <CardTitle>OVO 3</CardTitle>
              <CardHint>{t('Download for Windows')}</CardHint>
            </div>
          </ChoiceCard>
        </ButtonBlock>
      </ModalBlock>
    </Fullscreen>
  );
};

export default GPUDetectionModal;